import { defineDocument } from '../../schema';
export const featureAudioDocument = defineDocument({
    id: 'feature:audio',
    kind: 'feature',
    slug: 'audio',
    route: '/discover/features/audio',
    seo: {
        title: 'Discover Audio',
        description: 'Experience the Airhead Games audio landscape. Volume controls, original soundtracks, and immersive sound design.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Feature',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Audio',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Experience the Airhead Games audio landscape. Volume controls, original soundtracks, and immersive sound design.',
            },
        ],
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'audio-experience',
            title: [
                {
                    type: 'text',
                    text: 'The Audio Experience',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Audio is a core part of Airhead Games. Every audio element is designed to immerse your group in the game and elevate the energy of the room. There are three core aspects of the audio experience:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Poppy',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: " - Poppy's voice in your preferred ",
                            },
                            {
                                type: 'link',
                                href: '/discover/features/user-preferences#host-style__accent',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'accent',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ' to guide the game',
                            },
                        ],
                        [
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Music',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ' - Original soundtracks for the lobby and each game to set the mood',
                            },
                        ],
                        [
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Sound Effects',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ' - Audio cues and ambient sounds to complement the game action',
                            },
                        ],
                    ],
                },
            ],
            sections: [
                {
                    id: 'audio-experience__volume-controls',
                    title: [
                        {
                            type: 'text',
                            text: 'Volume Controls',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Each channel has its own volume. Set your mix from the ',
                                },
                                {
                                    type: 'link',
                                    href: '/library?section=volume-controls',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Library',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' sidebar, or during a game from ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/poppy#in-game-menu',
                                    children: [
                                        {
                                            type: 'text',
                                            text: "Poppy's Menu",
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: '. One mix carries across every room. Changes auto-save wherever you make them, so the levels you set mid-game are the levels your next room starts with.',
                                },
                            ],
                        },
                        {
                            type: 'image',
                            imageAsset: 'featureVolumeSliders',
                            imageAlt: 'Mute controls and volume sliders for each audio channel.',
                            caption: [
                                {
                                    type: 'text',
                                    text: 'Mute controls and volume sliders for each audio channel.',
                                },
                            ],
                            maxWidth: 600,
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Every channel starts at 100%, a baseline mix we tune to work well for most rooms. Your setup may call for something different: streamers and groups playing remotely often pull music down to leave room for their own commentary and voice chat. Experiment with different mixes to find what works for your setup.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Music ducking is separate from your mix. Whenever Poppy speaks, music drops automatically and returns once the speech ends, relative to whatever level you have set. That is built into how audio works here, not something you configure.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'audio-experience__mute-state',
                    title: [
                        {
                            type: 'text',
                            text: 'Mute State',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Default browser behavior is to mute audio playback until users interact with the website. Airhead Games respects this behavior and only starts audio playback automatically when a user would reasonably expect to hear audio (like when starting a game, previewing ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/user-preferences#host-style__preview-voice',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'host styles',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', or ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/music',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'discovering music',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ').',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Hosts can mute all audio with a single click in ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/poppy#in-game-menu',
                                    children: [
                                        {
                                            type: 'text',
                                            text: "Poppy's Menu",
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: '. Muting turns off all three audio channels at once. Unmuting resumes them at their current levels. While muted, the sliders are shown but cannot be changed.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'audio-experience__visualization',
                    title: [
                        {
                            type: 'text',
                            text: 'Visualization',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "Poppy helps to visualize audio from all three channels at all volume levels. Given the importance of Poppy's voice to the progression of the game, Poppy's mouth will continue to move even when the audio is muted and there will be a red glow around Poppy. On the other hand, muted music or sound effects will not be visualized by Poppy's mouth or show the red glow.",
                                },
                            ],
                        },
                        {
                            type: 'image',
                            imageAsset: 'featurePoppyMuted',
                            imageAlt: 'Poppy while muted.',
                            caption: [
                                {
                                    type: 'text',
                                    text: 'Poppy while muted.',
                                },
                            ],
                            maxWidth: 400,
                        },
                    ],
                },
            ],
        },
        {
            id: 'music',
            title: [
                {
                    type: 'text',
                    text: 'Music',
                },
            ],
            blocks: [],
            sections: [
                {
                    id: 'music__original-soundtracks',
                    title: [
                        {
                            type: 'text',
                            text: 'Original Soundtracks',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Airhead Games exclusively features original music. The lobby and each game have their own unique soundtrack, carefully curated to enhance the party game experience. While the final music output is AI-generated, the inputs (such as lyrics, instrumentation, styles, artist personas) are carefully crafted by the developer of Airhead Games.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "When a lobby is opened or a game is started, the corresponding track list will be shuffled and played. The playlist will continue to loop until the lobby or game is closed. Each game's soundtrack is intended to be long enough so that the same song is not repeated within the same game.",
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Explore the ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/music',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'full music library',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' to browse tracks by artist, game, and style.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'music__stream-safe-music',
                    title: [
                        {
                            type: 'text',
                            text: 'Stream-Safe Music',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'All music is stream-safe and cleared for use in broadcasts. Streamers never need to worry about copyright strikes or muted VODs when playing Airhead Games.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'music__display',
                    title: [
                        {
                            type: 'text',
                            text: 'Display',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'When a new track begins or ends, the song title and artist appear briefly in the bottom left corner of the screen to introduce players to our music.',
                                },
                            ],
                        },
                        {
                            type: 'image',
                            imageAsset: 'featureMusicDisplay',
                            imageAlt: 'Music display pop up showing song information during track transition',
                            caption: [
                                {
                                    type: 'text',
                                    text: 'Song information displays briefly in the bottom left corner when tracks change.',
                                },
                            ],
                            maxWidth: 600,
                        },
                    ],
                },
            ],
        },
        {
            id: 'sound-effects',
            title: [
                {
                    type: 'text',
                    text: 'Sound Effects',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Sound effects are intended to add polish and feedback to gameplay without being overwhelming or distracting. Timers, reveals, transitions, victories, and other animations may have corresponding sound effects to enhance the experience.',
                        },
                    ],
                },
            ],
        },
    ],
});
//# sourceMappingURL=audio.js.map